import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import { IcoSparkles16 } from '@onefootprint/icons';
import type { AgentVersionHistoryItem, AgentVersionInfo } from '@onefootprint/request-types/dashboard';
import { LinkButton, Tag } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useActorLabel from '@/hooks/use-actor-label';
import { useBuilder } from '../../store/provider';
import translateOperation from '../undo-redo/utils/translate-operation';
import useRelativeTimestamp from './use-relative-timestamp';
type HistoryRowProps = {
  item: AgentVersionHistoryItem;
  tab: 'history' | 'releases';
  current: AgentVersionInfo;
  onSelect: (item: AgentVersionHistoryItem) => void;
  onSelectVersion?: () => void;
};
const HistoryRow = t0 => {
  const $ = _c(59);
  const {
    item,
    tab,
    current,
    onSelect,
    onSelectVersion
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.history"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const {
    setPreviewVersionId
  } = useBuilder();
  const getActorLabel = useActorLabel();
  let t2;
  if ($[1] !== getActorLabel || $[2] !== item.createdBy) {
    t2 = getActorLabel(item.createdBy);
    $[1] = getActorLabel;
    $[2] = item.createdBy;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const actorLabel = t2;
  const relativeTimestamp = useRelativeTimestamp(item.createdAt);
  let t3;
  if ($[4] !== formatDateWithTime || $[5] !== item.createdAt) {
    t3 = formatDateWithTime(new Date(item.createdAt));
    $[4] = formatDateWithTime;
    $[5] = item.createdAt;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const fullTimestamp = t3;
  const isReleasesTab = tab === "releases";
  const isActiveRow = current.id === item.id;
  const isShowingOtherHistoricalRow = current.isHistoricalPreview && current.id !== item.id;
  let t4;
  bb0: {
    if (isReleasesTab) {
      t4 = null;
      break bb0;
    }
    if (!item.operation) {
      let t5;
      if ($[7] !== t) {
        t5 = t(_temp);
        $[7] = t;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t4 = t5;
      break bb0;
    }
    const op = item.operation as {
      kind?: string;
      restoredFromVersionId?: string;
    };
    if (op.kind === "replace_graph" && op.restoredFromVersionId) {
      const restoredFromId = op.restoredFromVersionId;
      const t5 = isActiveRow ? "label-3" : "body-3";
      let t6;
      if ($[9] !== onSelectVersion || $[10] !== restoredFromId || $[11] !== setPreviewVersionId) {
        t6 = e => {
          e.stopPropagation();
          setPreviewVersionId(restoredFromId);
          onSelectVersion?.();
        };
        $[9] = onSelectVersion;
        $[10] = restoredFromId;
        $[11] = setPreviewVersionId;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      let t7;
      if ($[13] !== t) {
        t7 = t(_temp3);
        $[13] = t;
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      let t8;
      if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
        t8 = <Trans ns="agents" i18nKey={_temp2} components={{
          link: <LinkButton variant={t5} className="!align-baseline" onClick={t6}>{t7}</LinkButton>
        }} />;
        $[15] = t5;
        $[16] = t6;
        $[17] = t7;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      t4 = t8;
      break bb0;
    }
    let t5;
    if ($[19] !== item.operation || $[20] !== item.suggestionDescription) {
      t5 = item.suggestionDescription || translateOperation(item.operation);
      $[19] = item.operation;
      $[20] = item.suggestionDescription;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    t4 = t5;
  }
  const title = t4;
  let t5;
  if ($[22] !== item || $[23] !== onSelect) {
    t5 = () => onSelect(item);
    $[22] = item;
    $[23] = onSelect;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const t6 = isActiveRow ? "opacity-100! border-accent bg-senary/70" : "border-transparent";
  const t7 = isShowingOtherHistoricalRow && "opacity-60";
  const t8 = item.isBeyondHead && "opacity-60";
  let t9;
  if ($[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = cx("relative flex flex-col gap-1 px-4 py-3 border-l-2 text-left w-full transition-colors duration-200 ease-in-out hover:bg-hover cursor-pointer", t6, t7, t8);
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== isActiveRow || $[30] !== item.suggestionDescription || $[31] !== title) {
    t10 = title && <span title={item.suggestionDescription || undefined} className={cx("text-body-3 text-primary min-w-0 truncate", isActiveRow && "text-label-3")}>{title}</span>;
    $[29] = isActiveRow;
    $[30] = item.suggestionDescription;
    $[31] = title;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== item.versionTag) {
    t11 = item.versionTag && <div className="shrink-0"><Tag>v{item.versionTag}</Tag></div>;
    $[33] = item.versionTag;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11) {
    t12 = <div className="flex items-center gap-2 min-w-0">{t10}{t11}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== item.publishNotes) {
    t13 = item.publishNotes && <p className="text-body-3 text-secondary">{item.publishNotes}</p>;
    $[38] = item.publishNotes;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== fullTimestamp || $[41] !== relativeTimestamp) {
    t14 = <span className="shrink-0" title={fullTimestamp}>{relativeTimestamp}</span>;
    $[40] = fullTimestamp;
    $[41] = relativeTimestamp;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span aria-hidden={true} className="shrink-0">·</span>;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== actorLabel) {
    t16 = <span className="min-w-0 truncate" title={actorLabel}>{actorLabel}</span>;
    $[44] = actorLabel;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== item.fromSuggestion || $[47] !== t) {
    t17 = item.fromSuggestion && <><span aria-hidden={true} className="shrink-0">·</span><span className="inline-flex items-center gap-1 text-accent shrink-0"><IcoSparkles16 color="accent" />{t(_temp4)}</span></>;
    $[46] = item.fromSuggestion;
    $[47] = t;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== t14 || $[50] !== t16 || $[51] !== t17) {
    t18 = <div className="flex items-center gap-1 text-caption-2 text-tertiary min-w-0">{t14}{t15}{t16}{t17}</div>;
    $[49] = t14;
    $[50] = t16;
    $[51] = t17;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t12 || $[54] !== t13 || $[55] !== t18 || $[56] !== t5 || $[57] !== t9) {
    t19 = <li className="contents"><button type="button" tabIndex={0} onClick={t5} className={t9}>{t12}{t13}{t18}</button></li>;
    $[53] = t12;
    $[54] = t13;
    $[55] = t18;
    $[56] = t5;
    $[57] = t9;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
};
export default HistoryRow;
function _temp($) {
  return $.initialVersion;
}
function _temp2($_0) {
  return $_0.agentBuilder.history.restoredFromOldVersion;
}
function _temp3($_1) {
  return $_1.oldVersion;
}
function _temp4($_2) {
  return $_2.viaPercy;
}